import { useState } from "react";
import Markdown, { RenderRules } from "react-native-markdown-display";
import { StackProps } from "@tamagui/core";
import { ChevronDown } from "@tamagui/lucide-icons";
import { Circle, styled, Text, Accordion as TGAccordion } from "tamagui";

const AccordionTrigger = styled(TGAccordion.Trigger, {
  name: "AccordionTrigger",
  paddingVertical: "$1.5",
  paddingHorizontal: "$4",
  $sm: {
    paddingVertical: "$1.3",
    paddingHorizontal: "$2",
  },
  variants: {
    variant: {
      default: {
        borderColor: "$gray-200",
        borderWidth: "$0.25",
        backgroundColor: "$warm-snow-500",
        hoverStyle: {
          backgroundColor: "white",
        },
        disabledStyle: {
          borderColor: "$gray-100",
          backgroundColor: "white",
          cursor: "not-allowed",
        },
      },
    },
    open: {
      true: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
      false: {},
    },
    rounded: {
      md: {
        borderRadius: "$7",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    rounded: "md",
  },
});

const AccordionContent = styled(TGAccordion.Content, {
  name: "AccordionContent",
  paddingTop: "$2",
  paddingHorizontal: "$3",
  paddingBottom: "$2",
  borderBottomWidth: "$0.25",
  borderLeftWidth: "$0.25",
  borderRightWidth: "$0.25",
  $sm: {
    paddingHorizontal: "$2",
  },
  variants: {
    variant: {
      default: {
        backgroundColor: "white",
        borderColor: "$gray-200",
      },
    },

    rounded: {
      md: {
        borderBottomLeftRadius: "$7",
        borderBottomRightRadius: "$7",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    rounded: "md",
  },
});

const tailModeRules: RenderRules = {
  paragraph: (node, children, _parent, styles) => {
    return (
      <Text
        textAlign="left"
        key={node.key}
        style={styles}
        ellipsizeMode="tail"
        numberOfLines={2}
      >
        {children}
      </Text>
    );
  },
};

interface AccordionData {
  id: string;
  title: string;
  content: unknown;
}

export function Accordion<T extends AccordionData>({
  data,
  children,
  disabled,
  ...rest
}: {
  data: T[];
  children?: (data: T) => React.ReactNode;
} & Omit<StackProps, "children">) {
  const [selected, setSelected] = useState<string[]>([]);

  if (!data || data.length === 0) return null;

  return (
    <TGAccordion
      onValueChange={setSelected}
      overflow="hidden"
      type="multiple"
      gap={12}
      {...rest}
    >
      {data.map((item) => (
        <TGAccordion.Item
          data-id={"accordion-item"}
          data-value={item.id}
          value={item.id}
          key={item.id}
        >
          <AccordionTrigger
            data-id={"accordion-trigger"}
            disabled={disabled}
            open={selected.includes(item.id)}
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {({ open }: { open: boolean }) => (
              <>
                <Markdown
                  rules={tailModeRules}
                  style={{
                    body: {
                      flexShrink: 1,
                      fontFamily: "Fixel Text",
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: 24,
                      color: disabled ? "#A1A7BA" : "#111111",
                    },
                  }}
                >
                  {item.title}
                </Markdown>
                <Circle
                  rotate={open ? "180deg" : "0deg"}
                  backgroundColor={"$gray-100"}
                  padding={"$0.5"}
                >
                  <ChevronDown
                    size="$3"
                    color={disabled ? "$gray-secondary-400" : "$gray-950"}
                  />
                </Circle>
              </>
            )}
          </AccordionTrigger>
          {/*TGAccordion.HeightAnimator is removed coz it cause trouble with dialog component*/}
          <AccordionContent
            animation={"medium"}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            data-id={"accordion-content"}
          >
            {children?.(item)}
          </AccordionContent>
        </TGAccordion.Item>
      ))}
    </TGAccordion>
  );
}
